import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { PageHero, ContentSection, SEOContent, CTASection } from '../components/shared';
import { pageHeroImages } from '../data/mock';
import { ArrowRight, ArrowDown } from 'lucide-react';

const kardiologieFeatures = [
  {
    id: 'herz-check',
    title: 'Herz-Check (Basis-Check)',
    description: 'Vorsorgeuntersuchung für ein sicheres Gefühl rund um Ihr Herz.',
    image: '/images/Kardiologie 2.webp',
    link: '/leistungen/kardiologie/herz-check'
  },
  {
    id: 'schrittmacher',
    title: 'Schrittmacher-MRT-Koordination',
    description: 'Sichere Planung und fachärztliche Abstimmung bei MRT mit Herzschrittmacher.',
    image: '/images/Herzschrittmacher.webp',
    link: '/leistungen/kardiologie/schrittmacher-mrt'
  },
  {
    id: 'gefaess',
    title: 'Gefäßdiagnostik (PAVK)',
    description: 'Abklärung von Durchblutungsstörungen an Arterien und Venen.',
    image: '/images/Gelenke.webp',
    link: '/leistungen/kardiologie/gefaessdiagnostik'
  },
  {
    id: 'praevention',
    title: 'Präventive Kardiologie',
    description: 'Herzgesundheit erhalten durch gezielte Vorsorge und Beratung.',
    image: '/images/Cardiologie.webp',
    link: '/leistungen/kardiologie/praevention'
  },
  {
    id: 'cardisiographie',
    title: '3D-EKG (Cardisiographie)',
    description: 'Moderne Herzdiagnostik zur erweiterten Beurteilung der Herzfunktion.',
    image: '/images/EKG_neu.webp',
    link: '/leistungen/kardiologie/cardisiographie'
  }
];

export const KardiologiePage = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      <PageHero
        title="Kardiologie & Herz-Check im Medicum Diez"
        subtitle="Moderne Herzdiagnostik und persönliche Betreuung an einem Standort."
        backgroundImage={pageHeroImages.kardiologie}
        ctaText="Herz-Termin buchen"
      />

      {/* Schnellnavigation - Anchor Buttons */}
      <section className="py-6 bg-[#032925]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => scrollToSection('leistungen')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Unsere Leistungen
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('info')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Herzmedizin
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('diagnostik')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Diagnostik
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('termin')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Termin buchen
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Feature Grid mit klickbaren Karten - Links zu Unterseiten */}
      <section id="leistungen" className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-[#032925]">
              Unsere kardiologischen Leistungen
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {kardiologieFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={feature.link} className="block h-full">
                  <div className="bg-[#8ae9bb]/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all h-full">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-[#032925] mb-2">{feature.title}</h3>
                      <p className="text-[#032925]/70 text-sm mb-4">{feature.description}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#032925] hover:text-[#1a1308]">
                        Mehr erfahren
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Herzmedizin Content */}
      <div id="info">
        <ContentSection
          title="Herzmedizin mit kurzen Wegen"
          content="Die kardiologische Betreuung erfolgt in enger Zusammenarbeit mit Dr. Kirchner, Facharzt für Kardiologie im Medicum Diez. Untersuchungen, Befunde und Therapieentscheidungen werden direkt abgestimmt – damit Sie sich sicher und gut begleitet fühlen."
          image="/images/2C_Hero_Kardiologie.webp"
          backgroundColor="white"
        />
      </div>

      {/* Moderne Diagnostik */}
      <div id="diagnostik">
        <section className="py-16 lg:py-20 bg-[#f9f3e2]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="/images/Kardiologie 2.webp"
                    alt="Kardiologie Diagnostik"
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-[#032925]">
                  Moderne Diagnostik für Ihr Herz
                </h2>
                <p className="text-[#032925]/80 leading-relaxed mb-6">
                  Von der Vorsorge bis zur weiterführenden Abklärung stehen Ihnen im Medicum Diez moderne Untersuchungsverfahren und kurze Wege zwischen Hausarzt, Kardiologie und Diagnostik zur Verfügung.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/leistungen/diagnostik" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                    Diagnostik-Zentrum
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/leistungen/diagnostik/mrt" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                    Offenes MRT
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      <SEOContent
        title="Kardiologie in Diez und Limburg"
        content="Im Medicum Diez bietet die Kardiologie Herz-Checks, Gefäßdiagnostik, präventive Kardiologie und moderne EKG-Verfahren. In enger Zusammenarbeit mit den Hausärzten erhalten Patientinnen und Patienten koordinierte Herzmedizin im Rhein-Lahn-Kreis."
      />

      <div id="termin">
        <CTASection
          title="Kardiologischen Termin vereinbaren"
          subtitle="Vereinbaren Sie Ihren Termin zur Herzdiagnostik online oder telefonisch."
          buttonText="Herz-Termin buchen"
        />
      </div>
    </Layout>
  );
};

export default KardiologiePage;
